import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NotifierService } from 'angular-notifier';
import { LoadingService } from './loading.service';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, finalize, Observable, tap } from 'rxjs';

export class ajaxCallReturnValue<T> {
  success: boolean;
  results: T;
  message: string;
}

export class DataTablesResponse<T> {
  recordsTotal: number;
  recordsFiltered: number;
  data: T[];
}

@Injectable()
export class LoggingInterceptor implements HttpInterceptor {
  private static readonly storageKey: string = 'X-NVMS-Info';

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (request.method == 'POST') {
      var sendData = localStorage.getItem(LoggingInterceptor.storageKey);
      const reqWithHeader = sendData
        ? request.clone({
            headers: request.headers.set(
              LoggingInterceptor.storageKey,
              sendData
            ),
          })
        : request.clone();

      return next.handle(reqWithHeader).pipe(
        tap({
          next: (event: HttpEvent<any>) => {
            if (event.type == HttpEventType.Response) {
              var keyData = event.headers.get(LoggingInterceptor.storageKey);
              if (keyData) {
                localStorage.setItem(LoggingInterceptor.storageKey, keyData);
              }
            }
          },
          error: (error) => {},
        })
      );
    }
    return next.handle(request);
  }

  static clear(): void {
    localStorage.removeItem(LoggingInterceptor.storageKey);
  }
}

@Injectable({
  providedIn: 'root',
})
export class ajaxCall {
  constructor(
    private http: HttpClient,
    private router: Router,
    private notifier: NotifierService,
    private loading: LoadingService
  ) {
    this.onlineSubject = new BehaviorSubject<boolean>(true);
  }

  private onlineSubject: BehaviorSubject<boolean>;

  public offlineWatch(): Observable<boolean> {
    return this.onlineSubject.asObservable();
  }

  public MakeCall<T>(
    url: string,
    object?: any,
    doLoading?: boolean
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      doLoading = typeof doLoading !== 'undefined' ? doLoading : true;

      if (doLoading) {
        this.loading.inc(url);
      }

      url = environment.apiDomain + '/data' + url;

      this.http.post<ajaxCallReturnValue<T>>(url, object).subscribe({
        next: (response) => {
          if (navigator.onLine !== this.onlineSubject.getValue()) {
            this.notifier.notify(
              'success',
              'You are connected to the internet.'
            );
            this.onlineSubject.next(navigator.onLine);
          }

          if (response.success) {
            resolve(response.results);
            return;
          }
          if (response.message) {
            if (doLoading) {
              this.notifier.notify('error', response.message);
            }
            //This is called when there is an error getting the field report definition, usually because it doesn't exist.
            //Output the error message.
            console.log(response.message);
          }
          reject(response);
        },
        error: (response) => {
          if (navigator.onLine !== this.onlineSubject.getValue()) {
            if (!navigator.onLine) {
              this.notifier.notify(
                'error',
                'You are not connected to the internet.'
              );
            }
            this.onlineSubject.next(navigator.onLine);
          }

          if (doLoading) {
            this.loading.dec(url);
          }

          if (response.status === 403 || response.status === 401) {
            this.router.navigateByUrl('/login');
            return;
          }

          reject();
        },
        complete: () => {
          if (doLoading) {
            this.loading.dec(url);
          }
        },
      });
    });
  }

  private readonly squareLogo: string = 'squareLogo';

  public GetContent(url: string = null): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: (data) => {
          const reader = new FileReader();
          reader.readAsDataURL(data);
          reader.onload = () => {
            resolve(reader.result.toString());
          };
        },
        error: () => {
          reject();
        },
      });
    });
  }

  public GetSquareLogo(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      var c = localStorage.getItem(this.squareLogo);
      if (c) {
        resolve(c);
        return;
      }

      this.GetContent('/images/square-logo.png').then(
        (data) => {
          localStorage.setItem(this.squareLogo, data);
          resolve(data);
        },
        () => {
          reject();
        }
      );
    });
  }
}
